/**
 * Hằng số và kiểu dùng chung giữa backend, Admin và Mini App.
 * Giá trị phải khớp CHECK constraint trong database/migrations.
 */

export const ROLES = ['super_admin', 'admin', 'manager', 'sales', 'dispatcher', 'viewer'] as const;
export type RoleName = (typeof ROLES)[number];

export const PRICING_MODES = ['fixed', 'quote_only', 'hybrid'] as const;
export type PricingMode = (typeof PRICING_MODES)[number];

export const RENTAL_PERIODS = ['day', 'week', 'month'] as const;
export type RentalPeriod = (typeof RENTAL_PERIODS)[number];

/** CR-03: giá trọn kỳ hay đơn giá/ngày áp dụng khi thuê theo kỳ đó. */
export const PRICE_BASES = ['per_period', 'per_day'] as const;
export type PriceBasis = (typeof PRICE_BASES)[number];

export const PRODUCT_STATUSES = ['draft', 'published', 'archived'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const REQUEST_STATUSES = [
  'pending',
  'processing',
  'waiting_customer',
  'quoted',
  'accepted',
  'rejected',
  'converted',
  'cancelled',
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const QUOTATION_STATUSES = [
  'draft',
  'sent',
  'viewed',
  'accepted',
  'rejected',
  'expired',
  'cancelled',
] as const;
export type QuotationStatus = (typeof QUOTATION_STATUSES)[number];

/** CR-05: phí là dòng báo giá. */
export const QUOTATION_ITEM_TYPES = ['rental', 'transport', 'handling', 'other'] as const;
export type QuotationItemType = (typeof QUOTATION_ITEM_TYPES)[number];

export const BOOKING_STATUSES = [
  'confirmed',
  'preparing',
  'delivering',
  'active',
  'returning',
  'completed',
  'cancelled',
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/** CR-06 */
export const EQUIPMENT_OPERATIONAL_STATUSES = [
  'active',
  'maintenance',
  'inactive',
  'retired',
] as const;
export type EquipmentOperationalStatus = (typeof EQUIPMENT_OPERATIONAL_STATUSES)[number];

export const BLOCK_TYPES = ['rental', 'maintenance', 'reserved', 'manual'] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

export const LOCATION_TYPES = [
  'province',
  'former_province',
  'district',
  'industrial_zone',
  'warehouse',
  'other',
] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];

export const SPEC_DATA_TYPES = ['number', 'text', 'boolean', 'option'] as const;
export type SpecDataType = (typeof SPEC_DATA_TYPES)[number];

export const PRICE_STATUSES = ['active', 'inactive'] as const;
export type PriceStatus = (typeof PRICE_STATUSES)[number];

/** Nhãn giá trên card/chi tiết sản phẩm – PRD 5.3 */
export const PRICE_LABELS = ['reference_price', 'contact_for_quote', 'paused'] as const;
export type PriceLabel = (typeof PRICE_LABELS)[number];

export const REQUEST_SOURCES = ['zalo_miniapp', 'website', 'admin', 'other'] as const;
export type RequestSource = (typeof REQUEST_SOURCES)[number];

/** Mã quyền theo module. Seed gán quyền cho vai trò trong apps/api/prisma/seed.ts */
export const PERMISSIONS = {
  USERS_READ: 'users.read',
  USERS_MANAGE: 'users.manage',
  ROLES_MANAGE: 'roles.manage',
  AUDIT_READ: 'audit.read',
  SETTINGS_READ: 'settings.read',
  SETTINGS_MANAGE: 'settings.manage',
  CATALOG_READ: 'catalog.read',
  CATALOG_MANAGE: 'catalog.manage',
  PRICES_READ: 'prices.read',
  PRICES_MANAGE: 'prices.manage',
  /** CR-09/D-03: giá nhập, lãi gộp – chỉ manager trở lên. */
  COST_READ: 'cost.read',
  COST_MANAGE: 'cost.manage',
  EQUIPMENT_READ: 'equipment.read',
  EQUIPMENT_MANAGE: 'equipment.manage',
  SUPPLIERS_READ: 'suppliers.read',
  SUPPLIERS_MANAGE: 'suppliers.manage',
  CUSTOMERS_READ: 'customers.read',
  CUSTOMERS_MANAGE: 'customers.manage',
  REQUESTS_READ: 'requests.read',
  REQUESTS_MANAGE: 'requests.manage',
  REQUESTS_ASSIGN: 'requests.assign',
  QUOTATIONS_READ: 'quotations.read',
  QUOTATIONS_MANAGE: 'quotations.manage',
  QUOTATIONS_APPROVE: 'quotations.approve',
  BOOKINGS_READ: 'bookings.read',
  BOOKINGS_MANAGE: 'bookings.manage',
  REPORTS_READ: 'reports.read',
} as const;
export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Envelope phản hồi thống nhất – PRD mục 9. */
export interface ApiError {
  code: string;
  message: string;
  details: unknown[];
}
export interface PageMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  meta: Record<string, unknown> | PageMeta;
  error: ApiError | null;
}
